/** Transient timing only: dictated text never enters a store or mirror. */
import {
  rebaseTimedSpeechSegments,
  type NativeTimedSpeechSegment,
  type TimedSpeechSegment,
} from "../voice-speech-timing";
import {
  useVoiceAttentionStore,
  voiceAttentionElapsedMs,
} from "../voice-attention-store";

export type DictationImageReference = {
  index: number;
  atMs: number;
  /** Visible number may continue an earlier paste into the same input draft. */
  number?: number;
};
type Progress = { atMs: number; characters: number };
export type DictationImageTiming = {
  progress: Progress[];
  speechSegments: TimedSpeechSegment[];
};
const MAX_PRIVATE_SPEECH_SEGMENTS = 512;

let timing: {
  attentionId: string;
  progress: Progress[];
  nativeSegments: NativeTimedSpeechSegment[];
  originUptimeMs: number | null;
} | null = null;

export function clearDictationImageTiming(): void {
  timing = null;
}

/** Retain the recording clock even when its first speech result is final. */
export function preserveDictationImageTiming(attentionId: string | null): void {
  const attention = useVoiceAttentionStore.getState();
  if (!attentionId || attention.recordingId !== attentionId) return;
  if (timing?.attentionId !== attentionId) {
    timing = {
      attentionId,
      progress: [],
      nativeSegments: [],
      originUptimeMs: attention.originUptimeMs,
    };
  } else if (attention.originUptimeMs !== null) {
    timing.originUptimeMs = attention.originUptimeMs;
  }
}

/** Owner only; retain counts, not partial text, for engines without timings. */
export function observeDictationImageTiming(
  kind: "partial" | "commit" | "final",
  text: string,
  speechSegments?: NativeTimedSpeechSegment[],
): void {
  const attention = useVoiceAttentionStore.getState();
  // Screenshot staging can end attention before final speech correction.
  const attentionId =
    attention.recordingId ?? (kind === "final" ? timing?.attentionId : null);
  if (!attentionId) return;
  if (timing?.attentionId !== attentionId) {
    timing = {
      attentionId,
      progress: [],
      nativeSegments: [],
      originUptimeMs: attention.originUptimeMs,
    };
  }
  if (attention.originUptimeMs !== null)
    timing.originUptimeMs = attention.originUptimeMs;
  if (speechSegments?.length)
    timing.nativeSegments = speechSegments.slice(-MAX_PRIVATE_SPEECH_SEGMENTS);
  if (kind === "final") return;
  const characters = text.trim().length;
  if (timing.progress[timing.progress.length - 1]?.characters === characters)
    return;
  timing.progress.push({ atMs: voiceAttentionElapsedMs(), characters });
  // Retain the whole recording's coverage, with bounded timing metadata.
  if (timing.progress.length > 512) {
    timing.progress = timing.progress.filter((_, index) => index % 2 === 0);
  }
}

export function takeDictationImageTiming(
  attentionId: string | null,
): DictationImageTiming {
  const saved = timing;
  timing = null;
  if (!saved || saved.attentionId !== attentionId) {
    return { progress: [], speechSegments: [] };
  }
  return {
    progress: saved.progress,
    speechSegments: rebaseTimedSpeechSegments(
      saved.nativeSegments,
      saved.originUptimeMs,
      MAX_PRIVATE_SPEECH_SEGMENTS,
    ),
  };
}

/** Ignore case/punctuation while retaining offsets into the actual final. */
function canonical(text: string) {
  let value = "";
  const offsets: number[] = [];
  let offset = 0;
  for (const character of text) {
    offset += character.length;
    const normalized = character.normalize("NFKC").toLowerCase();
    for (const letter of normalized) {
      if (!/[\p{L}\p{N}\p{M}]/u.test(letter)) continue;
      value += letter;
      for (let index = 0; index < letter.length; index += 1) {
        offsets.push(offset);
      }
    }
  }
  return { value, offsets };
}

function alignedSegments(text: string, segments: TimedSpeechSegment[]) {
  const final = canonical(text);
  let before = final.value.length;
  // Native timings may contain only the last 96 words. Match backwards so
  // a repeated phrase resolves to the timed suffix, not an earlier sentence.
  return [...segments]
    .reverse()
    .flatMap((segment) => {
      const needle = canonical(segment.text).value;
      if (!needle || !Number.isFinite(segment.start_ms + segment.end_ms))
        return [];
      const position = final.value.lastIndexOf(needle, before - needle.length);
      if (position < 0 || position + needle.length > before) return [];
      before = position;
      return [
        {
          ...segment,
          from: position === 0 ? 0 : final.offsets[position - 1]!,
          to: final.offsets[position + needle.length - 1]!,
        },
      ];
    })
    .reverse();
}

/** Finish the current word; punctuation belongs with the preceding phrase. */
function boundary(text: string, offset: number): number {
  let at = Math.max(0, Math.min(text.length, Math.round(offset)));
  if (at === 0) return 0;
  if (/[\uDC00-\uDFFF]/u.test(text[at] ?? "")) at += 1;
  const unspaced =
    /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;
  while (
    at < text.length &&
    !/\s/u.test(text[at]!) &&
    !unspaced.test(text[at]!)
  )
    at += 1;
  return at;
}

export function insertDictationImageReferences(
  text: string,
  references: DictationImageReference[],
  timing: DictationImageTiming,
): string {
  if (!text.trim() || references.length === 0) return text;
  const segments = alignedSegments(text, timing.speechSegments);
  const insertions: Array<{ offset: number; marker: string }> = [];
  let previousOffset = 0;
  for (const reference of references) {
    let offset: number | undefined;
    const first = segments[0];
    // Before a truncated timed suffix, use the live progress fallback.
    if (first && reference.atMs >= first.start_ms) {
      offset = first.from;
      for (const segment of segments) {
        if (segment.start_ms > reference.atMs) break;
        offset = segment.to;
        if (segment.end_ms >= reference.atMs) {
          // Whisper supplies phrase timings; interpolate within the phrase
          // and finish the nearest word instead of grouping every marker at
          // its end. Native single-word timings need no interpolation.
          if (
            /\s/u.test(segment.text.trim()) &&
            segment.end_ms > segment.start_ms
          ) {
            const fraction =
              (reference.atMs - segment.start_ms) /
              (segment.end_ms - segment.start_ms);
            offset = segment.from + fraction * (segment.to - segment.from);
          }
          break;
        }
      }
    }
    if (offset === undefined) {
      offset = 0;
      for (const progress of timing.progress) {
        if (progress.atMs > reference.atMs) break;
        offset = progress.characters;
      }
    }
    previousOffset = Math.max(previousOffset, boundary(text, offset));
    insertions.push({
      offset: previousOffset,
      marker: `[${reference.number ?? reference.index + 1}]`,
    });
  }
  let result = "";
  let cursor = 0;
  for (const insertion of insertions) {
    const prefix = text.slice(cursor, insertion.offset);
    result += prefix;
    if (result && !/\s$/u.test(result)) result += " ";
    result += insertion.marker;
    if (
      insertion.offset < text.length &&
      !/^\s/u.test(text.slice(insertion.offset))
    )
      result += " ";
    cursor = insertion.offset;
  }
  return result + text.slice(cursor);
}
